<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { infoBoxIdEnum } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'
import { useReportColumnTemplates } from '@/composables/report-column-template'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import EditModeToggle from '@/components/shared/EditModeToggle.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import TemplateCardGrid from '@/components/shared/TemplateCardGrid.vue'
import ReportColumnTemplateThumbnail from '@/components/accounting/ReportColumnTemplateThumbnail.vue'
import ReportColumnTemplateEdit from '@/components/accounting/ReportColumnTemplateEdit.vue'

const props = defineProps({
  reportType: {
    type: Number,
    required: false,
    default: null,
  },
})

const emit = defineEmits(['cancel', 'changed'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { templatesOf, isDefault, columnCount, enabledSections } = useReportColumnTemplates()
const { reportColumnTemplateDefaultFields } = accountingStore

const filterEnum = Object.freeze({ ALL: 0, AVAILABLE: 1, HIDDEN: 2 })

const reportType = ref(props.reportType ?? accountingStore.reportTypeEnum.STAFF_ACTIVITY)
const filter = ref(filterEnum.ALL)
const editMode = ref(false)
const listRef = ref(null)
const editingTemplate = ref(null)
const templateToDelete = ref(null)

const reportTypeOptions = accountingStore.getLocalizedPrintSettingTypes().map(type => ({ label: type.name, value: type.id }))
const typeTemplates = computed(() => templatesOf(reportType.value))
const availableCount = computed(() => typeTemplates.value.filter(template => template.is_available).length)
const filterOptions = computed(() => [
  { value: filterEnum.ALL, label: `${i18n.t('common.all')} · ${typeTemplates.value.length}` },
  { value: filterEnum.AVAILABLE, label: `${i18n.t('common.available')} · ${availableCount.value}` },
  { value: filterEnum.HIDDEN, label: `${i18n.t('common.hidden')} · ${typeTemplates.value.length - availableCount.value}` },
])
const filteredTemplates = computed(() => typeTemplates.value.filter(template =>
  filter.value === filterEnum.ALL || template.is_available === (filter.value === filterEnum.AVAILABLE)
))

const lockReason = (template) => {
  if (isDefault(template)) return i18n.t('error.validation.reportColumnTemplateDefaultUnavailable')
  if (template.is_available && availableCount.value === 1) return i18n.t('error.validation.reportColumnTemplateLastAvailable')
  return null
}
const summary = (template) => [
  i18n.t('common.report.columnsCount', columnCount(template.config)),
  i18n.t('common.report.sectionsCount', enabledSections(template.config).length),
  i18n.t('common.report.templateUsedIn', template.report_count ?? 0),
].join(' · ')

const reloadAsync = async() => {
  await accountingStore.fetchReportColumnTemplatesAsync()
  emit('changed')
}
onMounted(() => accountingStore.fetchReportColumnTemplatesAsync())

const onToggleAvailable = async(template, isAvailable) => {
  await accountingStore.upsertReportColumnTemplateAsync({ id: template.id, is_available: isAvailable })
  await reloadAsync()
}

const onSetDefault = async(template) => {
  if (!template.is_available) await accountingStore.upsertReportColumnTemplateAsync({ id: template.id, is_available: true })
  await accountingStore.updateCompanyDataAsync({ id: accountingStore.companyData.id, [reportColumnTemplateDefaultFields[template.document_type]]: template.id })
  await Promise.all([accountingStore.fetchCompanyDataAsync(null, true), reloadAsync()])
  notifySuccess(i18n.t('info.updatedSuccessfully'))
}

const onDuplicate = async(template) => {
  const { data } = await accountingStore.upsertReportColumnTemplateAsync({
    name: i18n.t('common.copyOf', { name: template.label }),
    document_type: template.document_type,
    config: template.config,
    print_template: template.print_template,
  })
  await reloadAsync()
  filter.value = filterEnum.ALL
  notifySuccess(i18n.t('info.createdSuccessfully'))
  highlightRowAsync(data.id, listRef)
  editingTemplate.value = typeTemplates.value.find(template => template.id === data.id)
}

const onNew = () => { editingTemplate.value = { document_type: reportType.value } }

const onEditUpdated = async(id) => {
  const isNewTemplate = !editingTemplate.value.id
  editingTemplate.value = null
  await reloadAsync()
  if (isNewTemplate) {
    filter.value = filterEnum.ALL
    highlightRowAsync(id, listRef)
  }
}

const onDeleteConfirmed = async() => {
  const template = templateToDelete.value
  templateToDelete.value = null
  editingTemplate.value = null
  await accountingStore.deleteReportColumnTemplateAsync(template.id)
  await reloadAsync()
}
</script>
<template>
  <q-card class="dialog-card-flex">
    <navigate-back :text="i18n.t('common.report.columnTemplate', 2)" @cancel="emit('cancel')">
      <template #right>
        <edit-mode-toggle v-if="isMobile && typeTemplates.length" v-model="editMode" />
        <q-btn
          style="padding: 0;"
          size="md"
          unelevated
          ripple
          rounded
          icon="mdi-plus-circle-outline"
          @click="onNew"
        />
      </template>
    </navigate-back>
    <q-card-section class="row items-center q-gutter-sm q-py-sm">
      <q-select
        v-model="reportType"
        class="col-12 col-sm-4"
        :options="reportTypeOptions"
        :label="i18n.t('common.report.steps.documentType')"
        emit-value
        map-options
        dense
      />
      <q-btn-toggle
        v-model="filter"
        :options="filterOptions"
        no-caps
        unelevated
        toggle-color="primary"
      />
    </q-card-section>
    <q-card-section class="q-pt-none q-pb-sm">
      <info-box :id="infoBoxIdEnum.REPORT_COLUMN_TEMPLATES" :message="i18n.t('info.reportColumnTemplatesHint')" />
    </q-card-section>
    <q-card-section ref="listRef" class="scroll col" :class="isMobile ? 'q-pa-none' : 'bg-grey-2'">
      <q-list v-if="isMobile">
        <q-item
          v-for="template in filteredTemplates"
          :key="template.id"
          class="q-py-sm q-pl-sm q-pr-md"
          :class="{ 'row-highlight': template.id === highlightedRowId }"
          :clickable="editMode"
          @click="editMode && (editingTemplate = template)"
        >
          <q-item-section avatar>
            <report-column-template-thumbnail :class="{ 'report-template-hidden': !template.is_available }" :template="template" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ template.label }}</q-item-label>
            <q-item-label caption>{{ summary(template) }}</q-item-label>
            <q-item-label v-if="isDefault(template) || template.is_confidential" class="row q-gutter-xs">
              <q-chip
                v-if="isDefault(template)"
                dense
                size="sm"
                color="primary"
                text-color="white"
                icon="mdi-star"
                :label="i18n.t('common.default')"
              />
              <q-chip
                v-if="template.is_confidential"
                dense
                size="sm"
                icon="mdi-lock-outline"
                :label="i18n.t('common.report.templateConfidential')"
              />
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle
              :model-value="template.is_available"
              :disable="!!lockReason(template)"
              dense
              @click.stop
              @update:model-value="value => onToggleAvailable(template, value)"
            />
          </q-item-section>
          <q-item-section v-if="editMode" side class="q-pl-sm">
            <q-btn
              v-if="!isDefault(template)"
              style="padding: 0;"
              size="md"
              flat
              rounded
              color="negative"
              icon="mdi-minus-circle-outline"
              @click.stop="templateToDelete = template"
            />
          </q-item-section>
        </q-item>
      </q-list>
      <template-card-grid
        v-else
        :items="filteredTemplates"
        :highlighted-id="highlightedRowId"
        :name-of="template => template.label"
        :lock-reason="lockReason"
        :available-label="i18n.t('common.report.templateAvailable')"
        @open="template => editingTemplate = template"
        @toggle-available="onToggleAvailable"
      >
        <template #badges="{ item: template }">
          <q-chip
            v-if="isDefault(template)"
            dense
            size="sm"
            color="primary"
            text-color="white"
            icon="mdi-star"
            :label="i18n.t('common.default')"
          />
          <q-chip
            v-if="template.is_confidential"
            dense
            size="sm"
            icon="mdi-lock-outline"
            :label="i18n.t('common.report.templateConfidential')"
          />
        </template>
        <template #paper="{ item: template }">
          <report-column-template-thumbnail class="report-template-card-thumbnail" :template="template" />
        </template>
        <template #caption="{ item: template }">{{ summary(template) }}</template>
        <template #menu="{ item: template }">
          <q-item clickable @click="editingTemplate = template">
            <q-item-section avatar><q-icon name="mdi-pencil-outline" /></q-item-section>
            <q-item-section>{{ i18n.t('common.edit') }}</q-item-section>
          </q-item>
          <q-item clickable @click="onDuplicate(template)">
            <q-item-section avatar><q-icon name="mdi-content-copy" /></q-item-section>
            <q-item-section>{{ i18n.t('common.duplicate') }}</q-item-section>
          </q-item>
          <q-item clickable :disable="isDefault(template)" @click="onSetDefault(template)">
            <q-item-section avatar><q-icon name="mdi-star-outline" /></q-item-section>
            <q-item-section>{{ i18n.t('common.setAsDefault') }}</q-item-section>
          </q-item>
          <q-item clickable :disable="isDefault(template)" @click="templateToDelete = template">
            <q-item-section avatar><q-icon name="mdi-delete-outline" color="negative" /></q-item-section>
            <q-item-section class="text-negative">{{ i18n.t('common.delete') }}</q-item-section>
          </q-item>
        </template>
      </template-card-grid>
    </q-card-section>

    <q-dialog
      :model-value="!!editingTemplate"
      class="dialog-sm"
      :maximized="isMobile"
      persistent
      @hide="editingTemplate = null"
    >
      <report-column-template-edit
        v-if="editingTemplate"
        :key="editingTemplate.id ?? 'new'"
        :model-value="editingTemplate"
        @cancel="editingTemplate = null"
        @update="onEditUpdated"
        @delete="templateToDelete = editingTemplate"
        @duplicate="onDuplicate(editingTemplate)"
        @set-default="onSetDefault(editingTemplate)"
      />
    </q-dialog>

    <confirm-dialog
      :model-value="!!templateToDelete"
      :message="i18n.t('common.confirmDeleteMessage', { item: templateToDelete?.label })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="value => { if (!value) templateToDelete = null }"
      @confirm="onDeleteConfirmed"
    />
  </q-card>
</template>
<style scoped lang="scss">
.report-template-card-thumbnail {
  width: 7rem;
  margin: 0 auto;
}

.report-template-hidden {
  opacity: .5;
  filter: grayscale(.8);
}
</style>
